import React from "react";
import { Check } from "lucide-react";
import { Button } from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";
import { CYCLE_PRICE_NOTES } from "../data/subscriptionPlans";
import type { SubscriptionPlan } from "../types/billing.types";

interface PlanCardProps {
  plan: SubscriptionPlan;
  /** What pressing the buy button asks for. The card decides nothing itself —
   *  buying is the screen's business, not one card's. */
  onSubscribe: (plan: SubscriptionPlan) => void;
  /** This card's purchase is in flight, so its button spins. Per card rather
   *  than per screen: the other card is still a live choice until this one
   *  goes through. */
  busy?: boolean;
  /** This is the cycle the workspace is already running on. The card says so
   *  and stops offering to sell it again — which of the two, and what counts
   *  as running, is the screen's to decide; see `BillingSection`. */
  active?: boolean;
}

/**
 * One cycle of Naurist Pro, as a card: what it costs a month, what that is off
 * the list price, everything it includes, and the way to buy it.
 *
 * The two cards are the same card — they carry the same feature list and the
 * same button, and differ only in the figures and in which one is being
 * recommended. The recommended one is lifted by a tinted face and a cyan
 * hairline as well as by its filled button: quietly, in the same family of
 * teals, so the cheaper cycle beside it still reads as a full product rather
 * than as the option that was greyed out.
 *
 * The other card's button is `planQuiet` — plain at rest, the way a filled
 * action beside an outlined one is this app's pairing everywhere for a
 * recommendation next to a real alternative, and deepening its border to teal
 * under the pointer so the card being considered is the one that looks live.
 *
 * Both cards wear a saving badge, and it is a figure rather than a boast —
 * what a year on that cycle actually saves, which is the one span the two can
 * be compared over; see `plansService`. The recommended card's is filled and
 * the other's outlined, so the pair still reads as one recommendation.
 *
 * A card can also be the plan already being paid for, which it says with a
 * pill on the heading and by taking its own button out of use: the one thing a
 * subscriber cannot usefully do with their own cycle is buy it a second time.
 */
const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  onSubscribe,
  busy = false,
  active = false,
}) => {
  // Ties the card's accessible name to the heading inside it, so the two cards
  // are told apart as regions rather than both read out as "Subscription plan".
  const headingId = `plan-${plan.id}-heading`;

  // The extra this cycle takes off, where it takes one — the year does, the
  // month does not; see `subscriptionPlans`.
  const priceNote = CYCLE_PRICE_NOTES[plan.id];

  return (
    <section
      aria-labelledby={headingId}
      className={`relative flex flex-col rounded-md border p-6 sm:p-7 3xl:p-8 ${
        plan.recommended
          ? "border-[#5BD3E1] bg-[#F8FEFF]"
          : "border-line bg-white"
      }`}
    >
      {plan.saving !== null && (
        // Inset from both edges rather than pinned to the corner: the card has
        // a hairline border, and a badge sitting on it would read as a tab
        // hanging off the card instead of a note on it.
        //
        // Both cycles are discounted and both say by how much — a year of it
        // either way, so the two figures are comparable; see `plansService`.
        // The recommended card's is filled and the other's is outlined in the
        // same teal, which is how the pair still reads as one recommendation
        // beside a real alternative rather than as two competing offers. The
        // quiet one is an outline and not a paler fill so its figure stays as
        // legible as the figure it is being compared with.
        <span
          className={`absolute right-5 top-5 rounded-full border px-3 py-1 text-[11px] 3xl:text-[12px] font-semibold uppercase tracking-wide ${
            plan.recommended
              ? "border-brand-dark bg-brand-dark text-white"
              : "border-brand-dark bg-white text-brand-dark"
          }`}
        >
          Save {formatPrice(plan.saving)}
        </span>
      )}

      <h3
        id={headingId}
        // Padded clear of the saving badge above it, which is positioned out of
        // the flow and would otherwise be run into by a heading this one has
        // grown a pill on.
        className={`flex flex-wrap items-center text-[13px] 3xl:text-[14px] font-semibold uppercase tracking-[0.06em] text-brand-dark ${
          plan.saving !== null ? "pr-28" : ""
        }`}
      >
        {plan.productName}
        {/* Read as a separator by eye and as nothing at all by a screen
            reader, which would otherwise announce a bullet between the two
            halves of one name. */}
        <span aria-hidden="true" className="px-2">
          &bull;
        </span>
        {plan.cycleLabel}

        {/* Inside the heading rather than beside it, so "Active" is part of
            what this card is called: a screen reader moving between the two
            regions is told which one is theirs, and does not have to go
            looking for a pill to find out. */}
        {active && (
          <span className="ml-3 rounded-full bg-brand-soft px-2.5 py-0.5 text-[11px] 3xl:text-[12px] font-semibold tracking-wide text-brand-dark">
            Active
          </span>
        )}
      </h3>

      {/* Baseline-aligned, so the small print sits on the same line the big
          figure stands on rather than floating against its middle. */}
      <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-[32px] sm:text-[36px] 3xl:text-[42px] font-bold leading-none text-ink">
          {formatPrice(plan.pricePerMonth)}
        </span>
        {/* Struck through by eye; said in words to a screen reader, which
            announces a `<s>` inconsistently and would otherwise read the old
            price as a second, contradictory one. */}
        <span className="sr-only">, was </span>
        <s className="text-[19px] 3xl:text-[22px] font-medium text-ink-subtle">
          {formatPrice(plan.listPricePerMonth)}
        </s>
        <span className="text-[15px] 3xl:text-[16px] text-ink-muted">
          /month
        </span>
        <span className="text-[12px] 3xl:text-[13px] text-ink-muted">
          + GST
        </span>
      </div>

      {priceNote && (
        // Under the price rather than inside its row: it is a note on the whole
        // figure, and a fourth item in a baseline-aligned wrapping row would
        // break the line the price stands on at the width the row runs out.
        //
        // Violet, alone on this card — the teals here are the brand and its
        // recommendation, and a saving inside a saving said in a fourth teal
        // would read as a third piece of the same claim rather than as the
        // aside it is.
        <p className="mt-1 text-[13px] 3xl:text-[14px] font-normal leading-[1.2] text-[#6234CC]">
          {priceNote}
        </p>
      )}

      <p className="mt-6 3xl:mt-7 text-[14px] 3xl:text-[15px] font-medium text-ink">
        One plan. Complete access
      </p>

      <ul className="mt-4 flex flex-col gap-3 3xl:gap-4">
        {plan.features.map((feature) => (
          <li key={feature.name} className="flex gap-2">
            {/* Nudged down to the cap height of the line beside it — centred on
                the line box it would sit a shade high against the text. */}
            <Check
              aria-hidden="true"
              strokeWidth={2.5}
              className="mt-[3px] h-4 w-4 3xl:h-[18px] 3xl:w-[18px] flex-shrink-0 text-brand"
            />
            <p className="text-[13px] 3xl:text-[14px] leading-[1.55] text-ink-muted">
              <span className="font-medium text-ink">{feature.name}</span>
              {" - "}
              {feature.description}
            </p>
          </li>
        ))}
      </ul>

      {/* Pushed to the bottom of whichever card is taller, so the two buttons
          line up however long the lists above them run. */}
      <div className="mt-auto pt-7 3xl:pt-8">
        <Button
          variant={plan.recommended ? "primary" : "planQuiet"}
          size="lg"
          fullWidth
          loading={busy}
          disabled={active}
          onClick={() => onSubscribe(plan)}
          // The two buttons name their own cycle — "Buy monthly", "Buy yearly"
          // — so what is being bought is on the thing that buys it rather than
          // only in the heading four inches above. The name adds the product
          // to that, with the visible words still in front of it, so anyone
          // driving the page by voice can ask for what they can read.
          aria-label={`Buy ${plan.productName} ${plan.cycleLabel}`}
        >
          Buy {plan.cycleLabel.toLowerCase()}
        </Button>
      </div>
    </section>
  );
};

export default PlanCard;
